<template>
  <Modal ref="modalRef" :ok-disabled="failedRef" @ok="changePlan">
    <template #title>Change my plan</template>
    <div class="modal-body">
      <div v-if="failedRef" class="alert alert-warning alert-sm" role="alert">
        Sorry, something went wrong. Please try again later.
      </div>
      <div>
        <template v-if="isNewRef">Are you sure you want to sign up for this plan?</template>
        <template v-else>Are you sure you want to switch to this plan?</template>
      </div>
      <div class="plan">
        <div>Plan</div>
        <div>{{ newPlanRef?.displayName }}</div>
        <div>Price</div>
        <div>
          ${{ (newPlanRef?.price ?? 0).toFixed(2) }}/month
        </div>
        <div>Billed</div>
        <div v-if="newPlanRef?.price && newPlanRef.termMonths">
          ${{ (newPlanRef.price * newPlanRef.termMonths).toFixed(2) }} for {{ newPlanRef.termMonths }} months
        </div>
      </div>
      <div class="notes">
        <p v-if="!isUpgradeRef">
          Your subscription will continue on the current plan until your next billing cycle ({{ subRef?.currentPeriodEnd?.toLocaleString() }})
        </p>
      </div>
      <div>Billing changes</div>
      <ul class="list-unstyled billing">
        <li v-for="item in billingItemsRef" :key="item.item" class="billing-item" :class="item.id">
          <div class="item">{{ item.item }}</div>
          <div class="amount">{{ item.amount.toFixed(item.precision ?? 2) }}</div>
          <div class="unit">{{ item.unit }}</div>
        </li>
      </ul>
    </div>
    <template #okText>Change plan</template>
  </Modal>
</template>

<style lang="scss" scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.plan {
  margin-left: 3rem;
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: 0.25rem;
  column-gap: 1rem;
  align-items: baseline;

  :nth-child(odd) {
    font-size: 0.875rem;
    color: #888;
  }
}

.notes {
  font-size: 0.875rem;
  color: #888;
}

.billing {
  max-width: 350px;
  display: grid;
  grid-template-columns: 1fr auto auto;
  row-gap: 0.5rem;
  font-size: 0.875rem;
  color: #888;

  .billing-item {
    display: contents;

    .amount {
      padding: 0 0.25rem;
      text-align: right;
    }

    .unit {
      padding-right: 0.25rem;
    }

    &.total {
      font-size: 1rem;
      color: black;

      & > * {
        margin-top: 0.25rem;
        position: relative;

        &::before {
          position: absolute;
          content: "";
          top: -0.5rem;
          left: 0;
          width: 100%;
          border-top: 1px #ddd solid;
        }
      }

      .amount, .unit {
        background-color: yellow;
      }
    }
  }
}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useSubscriptionStore } from '@/gp/SubscriptionStore'
import { defaultPlanId, subscriptionPlans } from '@/gp/GroupAdminModel'
import { LoadMode } from '@/util/AsyncData'
import Modal from '@/util/Modal.vue'

defineExpose({
  open,
  addFailed,
})

const emit = defineEmits<{
  (e: 'add', newPlanId: string): void
}>()

const newPlanIdRef = ref<string>()
const failedRef = ref(false)
const modalRef = ref<InstanceType<typeof Modal>>()

const subStore = useSubscriptionStore()

const subRef = computed(() => {
  const keyList = subStore.getAsyncKeyListForSelf(LoadMode.EnsureLoaded)?.data
  const subs = subStore.getLoadedSubscriptionList(keyList?.keys ?? [])
  return subs?.at(0) // HACK: assume max 1 subscription for now
})
const currentPlanIdRef = computed(() => subRef.value?.items.at(0)?.price?.productId ?? defaultPlanId)
const currentPlanRef = computed(() => subscriptionPlans.find(p => p.id == currentPlanIdRef.value)!)
const newPlanRef = computed(() => subscriptionPlans.find(p => p.id == newPlanIdRef.value))
const currentPriceRef = computed(() => currentPlanRef.value.price ?? 0)
const newPriceRef = computed(() => newPlanRef.value?.price ?? 0)
const isNewRef = computed(() => !subRef.value)
const isUpgradeRef = computed(() => newPriceRef.value > currentPriceRef.value)

interface BillingItem {
  id: string
  item: string
  amount: number
  precision?: number
  unit?: string
}

const billingItemsRef = computed(() => {
  const currentTermDays = subRef.value?.currentPeriodEnd?.diff(subRef.value.currentPeriodStart!, 'days').days ?? 0
  const remainingDays = subRef.value?.currentPeriodEnd?.diffNow('days').days ?? 0
  const items: BillingItem[] = []

  let total = 0
  if (isUpgradeRef.value && currentPriceRef.value > 0) {
    const currentTermPrice = currentPriceRef.value * currentPlanRef.value.termMonths!
    const credit = Math.round(currentTermPrice * (remainingDays / currentTermDays))
    items.push({ 
      id: 'creditDays', 
      item: 'Remaining time on current plan', 
      amount: remainingDays, 
      precision: 0, 
      unit: "days"
    })
    items.push({ 
      id: 'credit', 
      item: `Credit for remaining term`, 
      amount: credit, 
      unit: 'USD' 
    })
    total -= credit;
  }
  if (newPlanRef.value && newPriceRef.value > 0) {
    const newTermPrice = newPriceRef.value * newPlanRef.value.termMonths!    
    items.push({ 
      id: 'planCharge', 
      item: `New plan (${newPriceRef.value.toFixed(2)} x ${newPlanRef.value.termMonths} months)`, 
      amount: newTermPrice, 
      unit: "USD" 
    })
    total += newTermPrice
  }
  items.push({ 
    id: 'total', 
    item: 'Total billed today', 
    amount: total, 
    unit: 'USD' 
  })
  return items;
})

function open(newPlanId: string) {
  newPlanIdRef.value = newPlanId
  failedRef.value = false
  modalRef.value!.open()
}

async function changePlan() {
  if (!newPlanRef.value)
    return

  if (subRef.value) {
    try {
      if (newPlanRef.value.id == defaultPlanId) {
        await subStore.cancelAsync(subRef.value.id!)
      }
      else {
        await subStore.changePlanAsync(subRef.value!.id!, newPlanRef.value.id!)
      }
      modalRef.value!.close()
    }
    catch (error) {
      failedRef.value = true
      modalRef.value!.reset()
    }
  }
  else {
    emit('add', newPlanIdRef.value!) // let caller close modal
  }
}

function addFailed() {
  failedRef.value = true
  modalRef.value?.reset()
}
</script>
